import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

// Shared layout for /terms, /privacy, /acceptable-use.
// Renders a dark hero, a sticky Table-of-Contents on desktop, and the
// numbered body sections passed via children.
export default function LegalLayout({ title, effectiveDate, intro, toc, children }) {
  useEffect(() => { window.scrollTo(0, 0); }, [title]);
  return (
    <div className="min-h-screen bg-[#F9FAFB] text-[#0B0F19]">
      {/* HEADER */}
      <header className="bg-[#0B0F19] border-b border-white/5">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#6C3AED] flex items-center justify-center">
              <span className="text-white font-bold text-sm">M</span>
            </div>
            <span className="text-white font-bold text-lg tracking-tight">MyLikeness</span>
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-white/60 hover:text-white text-sm">Home</Link>
            <Link to="/login" className="text-white/60 hover:text-white text-sm">Sign in</Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="bg-[#0B0F19] pt-8 pb-16">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <Link to="/" className="inline-flex items-center gap-1.5 text-white/40 hover:text-white/80 text-xs mb-6 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to home
          </Link>
          <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            {title}
          </h1>
          <p className="text-white/40 text-xs uppercase tracking-[0.2em] mb-6">Effective {effectiveDate}</p>
          {intro && <p className="text-white/70 text-base max-w-3xl leading-relaxed">{intro}</p>}
        </div>
      </section>

      {/* BODY with TOC */}
      <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* TOC */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-6">
              <p className="text-[#0B0F19] text-xs font-bold tracking-[0.15em] uppercase mb-3">Contents</p>
              <nav className="space-y-1.5">
                {toc.map((t, i) => (
                  <a key={i} href={`#s-${t.id}`} className="block text-xs text-slate-500 hover:text-[#6C3AED] transition-colors leading-relaxed">
                    <span className="text-slate-400 mr-1.5">{i + 1}.</span>{t.label}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* CONTENT */}
          <article className="lg:col-span-9 max-w-none">
            <div className="prose prose-slate max-w-none">
              {children}
            </div>
          </article>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#0B0F19] py-8 border-t border-white/5">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/30 text-xs">© {new Date().getFullYear()} Mylikeness Pty Ltd</p>
          <div className="flex items-center gap-5 flex-wrap">
            <Link to="/terms" className="text-white/40 hover:text-white/80 text-xs transition-colors">Terms</Link>
            <Link to="/privacy" className="text-white/40 hover:text-white/80 text-xs transition-colors">Privacy</Link>
            <Link to="/acceptable-use" className="text-white/40 hover:text-white/80 text-xs transition-colors">Acceptable Use</Link>
            <Link to="/" className="text-white/40 hover:text-white/80 text-xs transition-colors">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export function Section({ id, title, children }) {
  return (
    <section id={`s-${id}`} className="mb-10 scroll-mt-6">
      <h2 className="text-xl font-bold text-[#0B0F19] mb-3 pb-2 border-b border-slate-200" style={{ fontFamily: "'Playfair Display', serif" }}>
        {title}
      </h2>
      <div className="text-slate-700 text-sm leading-[1.75] space-y-3">
        {children}
      </div>
    </section>
  );
}

export function Sub({ label, children }) {
  return (
    <div className="mt-3">
      <span className="font-semibold text-[#0B0F19]">{label}. </span>
      <span>{children}</span>
    </div>
  );
}
